import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Video } from "lucide-react";
import CinematicDemo from "../../components/demo/CinematicDemo";

const FONTS = {
  heading: "'Cabinet Grotesk', sans-serif",
  body: "'Manrope', sans-serif",
};

const DemoPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRecordMode = searchParams.get("record") === "true";

  // Recording mode — clean, chromeless, auto-play for screen capture
  if (isRecordMode) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="demo-record-mode">
        <div className="w-full max-w-[1280px]">
          <CinematicDemo variant="full" autoPlay={true} defaultMuted={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="demo-page">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 lg:px-12 py-4 border-b border-white/5">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors"
          data-testid="demo-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
        <img src="/yestimate-logo.jpeg" alt="Yestimate" className="h-8 w-auto rounded opacity-80" />
        <button
          onClick={() => navigate("/app/login")}
          className="text-sm text-white/60 hover:text-white font-medium transition-colors"
          data-testid="demo-nav-login"
        >
          Login
        </button>
      </nav>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-6 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/30 mb-3"
          style={{ fontFamily: FONTS.body }}>Product Demo</p>
        <h1 style={{ fontFamily: FONTS.heading }}
          className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-3">
          See how Yestimate turns inspections into authorised work
        </h1>
        <p className="text-base sm:text-lg text-white/40 max-w-2xl mx-auto" style={{ fontFamily: FONTS.body }}>
          Watch the complete workflow — from technician finding to customer approval — in under 90 seconds.
        </p>
      </div>

      {/* Demo Player */}
      <div className="max-w-5xl mx-auto px-6 pb-12">
        <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
          <CinematicDemo variant="full" autoPlay={false} />
        </div>
      </div>

      {/* Feature callouts */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Guided Inspection", desc: "Structured. Measured. No guesswork.", color: "#3B82F6" },
            { label: "Pre-Built Pricing", desc: "Findings become jobs automatically.", color: "#8B5CF6" },
            { label: "Job Intelligence", desc: "System catches what you'd miss.", color: "#10B981" },
            { label: "Customer Portal", desc: "Clear. Honest. Easy approval.", color: "#EC4899" },
          ].map((f) => (
            <div key={f.label} className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
              <div className="w-2 h-2 rounded-full mb-3" style={{ backgroundColor: f.color }} />
              <p className="text-sm font-semibold text-white/80">{f.label}</p>
              <p className="text-xs text-white/30 mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Video Export Section */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0">
              <Video className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white/80 mb-1">Export as Video</h3>
              <p className="text-xs text-white/40 mb-3">
                Need this demo as a video for ads, social, or email? Open recording mode — a clean, auto-playing view optimised for screen capture.
              </p>
              <a
                href="/demo?record=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-medium rounded-lg hover:bg-purple-500/20 transition-all"
                data-testid="demo-record-link"
              >
                <Video className="w-3.5 h-3.5" />
                Open Recording Mode
              </a>
              <p className="text-[10px] text-white/20 mt-2">
                Use Screen Studio, OBS, or your browser's built-in screen recorder to capture.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="border-t border-white/5 py-16 px-6 text-center">
        <h2 style={{ fontFamily: FONTS.heading }}
          className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
          Stop losing work. Start today.
        </h2>
        <p className="text-sm text-white/40 mb-8 max-w-md mx-auto">
          No credit card required. Your team is up and running in minutes.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate("/app/login")}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-neutral-900 text-sm font-bold rounded-lg hover:bg-neutral-100 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            data-testid="demo-cta-trial"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white/60 hover:text-white text-sm font-semibold rounded-lg transition-all hover:-translate-y-0.5"
            data-testid="demo-cta-book"
          >
            <Phone className="w-4 h-4" />
            Book a Live Demo
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-white/5 text-center">
        <p className="text-xs text-white/20">Yestimate — A better way to run your workshop.</p>
      </footer>
    </div>
  );
};

export default DemoPage;
